import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useModule } from "../../contexts/ModuleContext";
import { loginUser } from '../../services/authService';
import { setAuthData } from '../../utils/authStorage';
import { parseJwt } from "../../utils/jwtDecoder";
import Header from "../shared/Header";
import "./login.css";

const ShieldIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.5052 19.1088C7.74541 18.2534 5.43299 16.3464 4.06767 13.7999C2.70234 11.2535 2.39374 8.2721 3.20856 5.5C6.06446 5.63069 8.85904 4.64436 11.0002 2.75C13.1414 4.64436 15.936 5.63069 18.7919 5.5C19.414 7.61668 19.3853 9.87153 18.7094 11.9717M13.75 17.4167L15.5833 19.25L19.25 15.5833" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function LoginPage() {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { setActiveProject } = useModule();

  const validate = () => {
    const newErrors = {};
    if (!username.trim()) {
      newErrors.username = t("validation.usernameRequired");
    } else if (username.length < 3) {
      newErrors.username = t("validation.usernameMinLength");
    } else if (username.length > 20) {
      newErrors.username = t("validation.usernameMaxLength");
    }
    if (!password.trim()) {
      newErrors.password = t("validation.passwordRequired");
    } else if (password.length < 6) {
      newErrors.password = t("validation.passwordMinLength");
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const data = await loginUser(username, password);
      if (data) {
        const decoded = parseJwt(data.access);
        const role = decoded?.role || data?.role || '';
        const domain = decoded?.domain || data?.domain || '';
        const tenantId = decoded?.tenant_id || data?.tenant_id || decoded?.tenant || data?.tenant || '';
        const isBootstrapAdmin = Boolean(decoded?.is_bootstrap_admin || data?.is_bootstrap_admin || domain === 'public');
        setAuthData({
          access: data.access,
          refresh: data.refresh,
          username: decoded?.username || data?.username,
          role,
          domain,
          tenant_id: tenantId,
          is_bootstrap_admin: isBootstrapAdmin,
        }, keepSignedIn);

        // Persisted project/survey may belong to a previous user or tenant
        setActiveProject(null);

        // Post-login redirect based on role and domain
        if (isBootstrapAdmin) {
          navigate('/tenant-management');
        } else if (domain === 'construction') {
          navigate('/construction/projects');
        } else {
          navigate('/dashboard'); // surveillance
        }
      }
    } catch (error) {
      if (error.message === "No active account found with the given credentials.") {
        error.message = t("validation.invalidCredentials");
      }
      setErrors({ password: error.message });
    }
  };

  return (
    <div className={`login-page ${isDarkMode ? "dark" : "light"}`}>
      <Header
        title={t("brand.product")}
        leftNode={
          <span className="logo-icon-wrapper">
            <ShieldIcon size={22} />
          </span>
        }
        showLive={false}
        showTheme={true}
      />
      <div className="login-wrapper">
        <div className="form-container-login">
          <div className="login-header">
            <h1 className="login-title">{t("label.signIn")}</h1>
            <p className="login-subtitle">{t("label.accessAccount")}</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="login-input-group">
              <label className="login-input-label" htmlFor="InputUsername">{t("label.username")}</label>
              <input
                type="text"
                className="login-input"
                id="InputUsername"
                placeholder={t("placeholder.enterUsername")}
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              {errors.username && (
                <span className="login-error-text">{errors.username}</span>
              )}
            </div>

            <div className="login-input-group">
              <label className="login-input-label" htmlFor="InputPassword">{t("label.password")}</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  className="login-input"
                  id="InputPassword"
                  placeholder={t("placeholder.enterPassword")}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? t("ariaLabel.hidePassword") : t("ariaLabel.showPassword")}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <span className="login-error-text">{errors.password}</span>
              )}
            </div>

            <button type="submit" className="login-btn">
              {t("button.signIn")}
            </button>

            <div className="form-options-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox-input"
                  checked={keepSignedIn}
                  onChange={(e) => setKeepSignedIn(e.target.checked)}
                />
                {t("label.keepSignedIn")}
              </label>
              <a
                href="#forgot"
                className="forgot-password-link"
                onClick={(e) => e.preventDefault()}
              >
                {t("label.forgotPassword")}
              </a>
            </div>

            <hr className="form-divider" />

            <div className="status-container">
              <span className="status-dot"></span>
              <span>{t("label.allServicesOperational")}</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
